// Pure view helpers for the Terminal status line (#4395, epic #4391) — split
// out of `TerminalStatusLine.tsx` so that component's own render loop stays
// under the 120-line function cap. Every function here reads a `TerminalStatus`
// and answers a plain value (a word, a tone, a tooltip sentence); no DOM, no
// signals, and every string still goes through `t()` — this is a data-layer
// module rendering copy, exactly the pattern `lib/terminal-events.ts`'s
// `actorLabel` / `levelLabel` already use.

import type { TerminalSubjectKind } from '@developerz/domain';
import { t, tPlural } from '../i18n';
import { formatDurationMs, PENDING_WARN_MS, type TerminalStatus } from './terminal-status';
import { UNSET } from './unset';

export type StatusTone = 'warning' | 'danger' | undefined;

/**
 * Which `subject.kind` each status-line cell narrows a pane to; `status`
 * narrows nothing. A pane remembers the CELL it pressed, not the kind
 * (`use-terminal-panes.ts`'s `statusCellFor`): `queue`, `pending` and
 * `retries` share `'task'`, so the kind alone cannot say which one is pressed.
 */
export const STATUS_CELL_KIND = {
  status: null,
  workers: 'session',
  keys: 'key',
  vms: 'box',
  queue: 'task',
  prs_24h: 'pull_request',
  pending: 'task',
  retries: 'task',
} as const satisfies Readonly<Record<string, TerminalSubjectKind | null>>;

export type TerminalStatusCell = keyof typeof STATUS_CELL_KIND;

export function statusWord(status: TerminalStatus): string {
  if (status.status === 'ok') return t('terminal.status_ok');
  if (status.status === 'degraded') return t('terminal.status_degraded');
  return t('terminal.status_down');
}

export function statusTone(status: TerminalStatus): StatusTone {
  return status.status === 'degraded' ? 'warning' : status.status === 'down' ? 'danger' : undefined;
}

export function statusTitle(status: TerminalStatus): string {
  return t('terminal.status_tooltip', status.fail_rate_last_hour);
}

export function workersRunning(status: TerminalStatus): number {
  return status.workers.by_class.reduce((sum, c) => sum + c.running, 0);
}

export function workersTitle(status: TerminalStatus): string {
  const parts = status.workers.by_class.map((c) =>
    t('terminal.workers_tooltip_class_item', { class: c.class, count: c.running }),
  );
  return parts.length > 0
    ? parts.join(', ')
    : t('terminal.workers_tooltip_online', {
        online: status.workers.online,
        total: status.workers.total,
      });
}

export function vmsTitle(status: TerminalStatus): string {
  return t('terminal.workers_tooltip_online', {
    online: status.workers.online,
    total: status.workers.total,
  });
}

export function keysTone(status: TerminalStatus): StatusTone {
  if (status.keys.subs_exhausted) return 'danger';
  return status.keys.cooling > 0 ? 'warning' : undefined;
}

export function keysTitle(status: TerminalStatus): string {
  return status.keys.subs_exhausted
    ? t('terminal.keys_tooltip_exhausted')
    : t('terminal.keys_tooltip_usable', {
        usable: status.keys.usable,
        cooling: status.keys.cooling,
      });
}

export function queueTitle(status: TerminalStatus): string {
  return status.queue.head_class === null
    ? t('terminal.queue_tooltip_empty')
    : t('terminal.queue_tooltip_head', { class: status.queue.head_class });
}

export function pendingValue(status: TerminalStatus): string {
  const age = status.queue.oldest_pending_age_ms;
  return age === null ? UNSET : formatDurationMs(age);
}

export function pendingTone(status: TerminalStatus): StatusTone {
  const age = status.queue.oldest_pending_age_ms;
  return age !== null && age > PENDING_WARN_MS ? 'warning' : undefined;
}

export function pendingTitle(status: TerminalStatus): string {
  const age = status.queue.oldest_pending_age_ms;
  return age === null
    ? t('terminal.pending_tooltip_none')
    : t('terminal.pending_tooltip_age', { age: formatDurationMs(age) });
}

export function prsTitle(status: TerminalStatus): string {
  return t('terminal.prs_tooltip', status.prs_24h);
}

/**
 * RETRIES/hr's own hover breakdown (#4395 review — the cell shipped with
 * `title=""`, the one cell of eight step 4 asked a tooltip for that had one).
 */
export function retriesTitle(status: TerminalStatus): string {
  const n = status.retries_last_hour;
  return tPlural('terminal.retries_tooltip', n, { count: n });
}
